import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check, Phone, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo, SITE_URL } from "@/components/Seo";
import { CarCard } from "@/components/CarCard";
import { Button } from "@/components/ui/button";
import { FaqList, JsonLd, faqJsonLd } from "@/components/FaqList";
import { FAQ, LANDINGS, LANDING_LINKS } from "@/lib/seoPages";

export default function LandingPage() {
  const slug = useLocation().pathname.replace(/^\//, "");
  const p = LANDINGS[slug];
  const [cars, setCars] = useState([]);
  useEffect(() => { api.get("/cars").then((r) => setCars(r.data)).catch(() => {}); }, []);
  const faqs = useMemo(() => p.faq.map((i) => FAQ[i]), [p]);
  const crumbs = useMemo(() => ({
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: p.h1, item: `${SITE_URL}/${slug}` },
    ],
  }), [p, slug]);
  const faqLd = useMemo(() => faqJsonLd(faqs), [faqs]);

  return (
    <div className="min-h-screen bg-background grain" data-testid={`landing-${slug}`}>
      <Seo title={p.title} description={p.description} path={`/${slug}`} />
      <JsonLd id="ld-faq" data={faqLd} />
      <JsonLd id="ld-breadcrumb" data={crumbs} />
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-32 pb-12">
        <nav className="text-xs text-muted-foreground mb-6" aria-label="breadcrumb"><Link to="/" className="hover:text-foreground">Home</Link> / <span>{p.h1}</span></nav>
        <div className="font-mono-tech text-xs tracking-[0.3em] uppercase amber mb-4">{p.kicker}</div>
        <h1 className="font-display text-[1.7rem] sm:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase leading-none max-w-4xl break-words" data-testid="landing-h1">{p.h1}</h1>
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 mt-10">
          <div className="space-y-4 text-base text-slate-300 leading-relaxed">
            {p.intro.map((t) => <p key={t}>{t}</p>)}
            <div className="flex flex-wrap gap-3 pt-4">
              <Button asChild className="bg-[#E63946] hover:bg-[#D62828] text-white rounded-full px-7 h-12 font-semibold" data-testid="landing-cta">
                <Link to="/cars">Prenota ora <ArrowRight className="w-5 h-5 ml-2" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full h-12 px-6 border-white/15 bg-transparent" data-testid="landing-call">
                <a href="tel:+390587484791"><Phone className="w-4 h-4 mr-2" />0587 484791</a>
              </Button>
            </div>
          </div>
          <ul className="rounded-2xl border border-white/10 bg-[#181A22] p-7 space-y-3" data-testid="landing-points">
            {p.points.map((t) => <li key={t} className="flex gap-3 text-sm"><Check className="w-5 h-5 text-[#10B981] shrink-0" />{t}</li>)}
            <li className="flex gap-3 text-sm pt-3 border-t border-white/10"><MapPin className="w-5 h-5 amber shrink-0" />Via Don Primo Mazzolari 34/38, 56025 Pontedera (PI)</li>
          </ul>
        </div>
      </section>

      {cars.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-10">
          <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase mb-8">Auto disponibili a noleggio</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{cars.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}</div>
        </section>
      )}

      <section className="max-w-4xl mx-auto px-6 py-12">
        <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase mb-6">Domande frequenti</h2>
        <FaqList items={faqs} />
        <Link to="/domande-frequenti" className="inline-flex items-center gap-1 text-sm text-[#E63946] mt-5 hover:underline" data-testid="landing-all-faq">Tutte le domande <ArrowRight className="w-4 h-4" /></Link>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-4">
        <div className="flex flex-wrap gap-2">
          {LANDING_LINKS.filter(([to]) => to !== `/${slug}`).map(([to, label]) => (
            <Link key={to} to={to} className="text-xs rounded-full border border-white/10 px-4 py-2 text-muted-foreground hover:text-foreground hover:border-white/30 transition-colors">{label}</Link>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
